import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, lastValueFrom, of } from 'rxjs';
import * as Entities from '../../entities/index';
import { ListResponse } from 'app/_shared/models/list-response.model';
import { Result } from 'app/_shared/models/api.model';
import { LoggerService } from '../logging/logger.service';
import { CallCenterPhoneNumberImportRequest } from 'app/_shared/entities/entities';
import { ConfigurationService } from '../config/config.service';

@Injectable({
  providedIn: 'root',
})
export class CallCenterPhoneNumbersService {

  private serviceBase: string = undefined;
  public CallCenterPhoneNumbers: BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumber>> = new BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumber>>({records: []} as ListResponse<Entities.CallCenterPhoneNumber>);
  public CallCenterPhoneNumberTypes: BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumberType>> = new BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumberType>>({records: []} as ListResponse<Entities.CallCenterPhoneNumberType>);
  public UnallocatedCallCenterPhoneNumbers: BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumber>> = new BehaviorSubject<ListResponse<Entities.CallCenterPhoneNumber>>({records: []} as ListResponse<Entities.CallCenterPhoneNumber>);

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private configService: ConfigurationService
    ) {
    this.configService.Config.subscribe((config) => {
      this.serviceBase = `${config.apiUri}/callCenterPhoneNumbers/`;
    });
  }

  async loadCallCenterPhoneNumbers(force: boolean = false): Promise<void> {
    if (!force && this.CallCenterPhoneNumbers.value.records.length > 0) {
      return;
    }
    var result = await lastValueFrom(this.http.get<Result<ListResponse<Entities.CallCenterPhoneNumber>>>(this.serviceBase + 'list'));
    if (result.successful) {
      this.CallCenterPhoneNumbers.next(result.data);
      const unallocated = result.data.records.filter(c => c.allocated === null && c.callCenterPhoneNumberTypeId === 1);
      this.UnallocatedCallCenterPhoneNumbers.next({records: unallocated} as ListResponse<Entities.CallCenterPhoneNumber>);
    }
  }

  async import(request: CallCenterPhoneNumberImportRequest[]): Promise<Result<number>> {
    var result = await lastValueFrom(this.http.post<Result<number>>(this.serviceBase + 'import', request));
    if (result.successful) {
      this.loadCallCenterPhoneNumbers(true);
      if(result.errors.length > 0) {
        this.log.displayError('Call Center Phone Number was successful but had the following errors', 'Call Center Phone Number');
      }
    } else {
      this.log.displayError('Call Center Phone Number Import Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async loadCallCenterPhoneNumberTypes(force: boolean = false): Promise<void> {
    if (!force && this.CallCenterPhoneNumberTypes.value.records.length > 0) {
      return;
    }
    var result = await lastValueFrom(this.http.get<Result<ListResponse<Entities.CallCenterPhoneNumberType>>>(this.serviceBase + 'list-types'));
    if (result.successful) {
      this.CallCenterPhoneNumberTypes.next(result.data);
    }
  }

  async create(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
    console.debug('create', callCenterPhoneNumber);
    var result = await lastValueFrom(this.http.post<Result<number>>(this.serviceBase, callCenterPhoneNumber));
    if (result.successful) {
      this.loadCallCenterPhoneNumbers(true);
    } else {
      this.log.displayError('Call Center Phone Number Creation Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async update(id: number, program: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
    var result = await lastValueFrom(this.http.put<Result<number>>(this.serviceBase + id, program));
    if (result.successful) {
      this.log.displaySuccess('Call Center Phone Number Updated', 'Call Center Phone Number');
      this.loadCallCenterPhoneNumbers(true);
    } else {
      this.log.displayError('Call Center Phone Number Update Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async delete(id: number): Promise<Result<number>> {
    var result = await lastValueFrom(this.http.delete<Result<number>>(this.serviceBase + id));
    if (result.successful) {
      this.loadCallCenterPhoneNumbers(true);
    } else {
      this.log.displayError('Call Center Phone Number Deletion Failed', 'Call Center Phone Number');
    }
    return result;
  }

  fetchAll(): Observable<Entities.CallCenterPhoneNumber[]> {
    return this.http.get<Entities.CallCenterPhoneNumber[]>(this.serviceBase);
  }

  fetchById(callCenterPhoneNumberId: number): Observable<Entities.CallCenterPhoneNumber> {
    return this.http.get<Entities.CallCenterPhoneNumber>(this.serviceBase + callCenterPhoneNumberId);
  }

  fetchPhoneNumberTypes(): Observable<Entities.CallCenterPhoneNumberType[]> {
    return this.http.get<Entities.CallCenterPhoneNumberType[]>(this.serviceBase + 'getPhoneNumberTypes');
  }

  getById(callCenterPhoneNumberId: number): Entities.CallCenterPhoneNumber {
    return this.CallCenterPhoneNumbers.value.records.find(c => c.callCenterPhoneNumberId === callCenterPhoneNumberId);
  }

  // async allocate(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
  //   var result = await lastValueFrom(this.http.put<Result<number>>(this.serviceBase + 'allocate/' + callCenterPhoneNumber.callCenterPhoneNumberId, JSON.stringify(callCenterPhoneNumber), { headers: { 'Content-Type': 'application/json' } }))
  //   if (result.successful) {
  //     return result;
  //   } else {
  //     this.log.displayError('Call Center Phone Number Allocation Failed', 'Call Center Phone Number');
  //   }
  // }

  // updateCallCenterPhoneNumber(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Observable<Entities.CallCenterPhoneNumber | string> {
  //   return this.http.put<Entities.CallCenterPhoneNumber>(this.serviceBase + callCenterPhoneNumber.callCenterPhoneNumberId, JSON.stringify(callCenterPhoneNumber), { headers: { 'Content-Type': 'application/json' } })
  //     .pipe(map((response: Entities.CallCenterPhoneNumber) => {
  //       return response;
  //     }), catchError(handleError<Entities.CallCenterPhoneNumber>('PutCallCenterPhoneNumber', null)));
  // }
}
